import type { ReactNode } from 'react';

/**
 * Les icônes au trait de l'ordinateur, celles des maquettes du carnet (`accueil.html`,
 * `projet-ouvert.html`) : la barre de gauche (chantier navigation), le rail des angles d'un projet
 * (chantier angles-du-projet, ticket 04) et l'accueil les lisent ici. Un seul dessin par icône :
 * Maintenant a la même horloge dans la barre et dans le rail, l'Agenda le même calendrier.
 *
 * Chaque icône prend la classe de l'écran qui la pose : sa taille et son trait (`fill: none`,
 * `stroke: currentColor`, 1,9) sont dans la feuille de cet écran, la couleur suit le texte.
 * Nommées par ce qu'elles dessinent quand deux écrans leur donnent deux sens (l'horloge est
 * Maintenant dans la barre, « J'attends » à l'accueil).
 */
type Proprietes = { className: string };

function Icone({ className, children }: Proprietes & { children: ReactNode }) {
  return (
    <svg className={className} viewBox="0 0 24 24" aria-hidden="true">
      {children}
    </svg>
  );
}

/** Le tableau de bord : quatre cases. */
export function IconeTableau({ className }: Proprietes) {
  return (
    <Icone className={className}>
      <path d="M4 4h7v7H4zM13 4h7v4h-7zM13 10h7v10h-7zM4 13h7v7H4z" />
    </Icone>
  );
}

/** Maintenant dans la barre et le rail, « J'attends » à l'accueil. */
export function IconeHorloge({ className }: Proprietes) {
  return (
    <Icone className={className}>
      <circle cx="12" cy="12" r="8" />
      <path d="M12 8v4l3 2" />
    </Icone>
  );
}

/** L'Agenda, dans la barre, le rail et l'accueil (« Ça arrive »). */
export function IconeCalendrier({ className }: Proprietes) {
  return (
    <Icone className={className}>
      <rect x="4" y="5" width="16" height="15" rx="2" />
      <path d="M4 9h16M9 3v4M15 3v4" />
    </Icone>
  );
}

/** Les projets : un dossier. */
export function IconeDossier({ className }: Proprietes) {
  return (
    <Icone className={className}>
      <path d="M4 7h6l2 2h8v10H4z" />
    </Icone>
  );
}

/**
 * Un dossier de l'arbre d'un projet, au téléphone (chantier telephone-projet, 02, maquette
 * `app-au-telephone.html`, `icD`) : une branche, ce qui contient quelque chose.
 */
export function IconeDossierDuCarnet({ className }: Proprietes) {
  return (
    <Icone className={className}>
      <path d="M3 6.5A1.5 1.5 0 0 1 4.5 5H9l2 2.2h8.5A1.5 1.5 0 0 1 21 8.7v9.8a1.5 1.5 0 0 1-1.5 1.5h-15A1.5 1.5 0 0 1 3 18.5z" />
    </Icone>
  );
}

/** Un fichier de l'arbre d'un projet, au téléphone (maquette `icF`) : une tâche, et sa fiche en page. */
export function IconeFichierDuCarnet({ className }: Proprietes) {
  return (
    <Icone className={className}>
      <path d="M6 3h8l4 4v14H6z" />
      <path d="M14 3v4h4M9 12h6M9 16h4" />
    </Icone>
  );
}

/** Les ressources : une feuille cornée. */
export function IconeFeuilleCornee({ className }: Proprietes) {
  return (
    <Icone className={className}>
      <path d="M6 3h9l4 4v14H6z" />
      <path d="M15 3v4h4" />
    </Icone>
  );
}

/** La boîte d'entrée (chantier boite-d-entree, ticket 02) : un bac à courrier, son creux au milieu. */
export function IconeBoite({ className }: Proprietes) {
  return (
    <Icone className={className}>
      <path d="M4 13l2.5-8h11L20 13v6H4z" />
      <path d="M4 13h4.5l1 2.5h5l1-2.5H20" />
    </Icone>
  );
}

/** Le profil : une silhouette. */
export function IconeSilhouette({ className }: Proprietes) {
  return (
    <Icone className={className}>
      <circle cx="12" cy="8" r="4" />
      <path d="M4 21c1-4 4-6 8-6s7 2 8 6" />
    </Icone>
  );
}

/** Les paramètres : un rouage. */
export function IconeRouage({ className }: Proprietes) {
  return (
    <Icone className={className}>
      <circle cx="12" cy="12" r="3" />
      <path d="M12 2v3M12 19v3M2 12h3M19 12h3M5 5l2 2M17 17l2 2M5 19l2-2M17 7l2-2" />
    </Icone>
  );
}

/** Ajouter : une croix droite. */
export function IconePlus({ className }: Proprietes) {
  return (
    <Icone className={className}>
      <path d="M12 5v14M5 12h14" />
    </Icone>
  );
}

/** La Carte : un centre et ses bulles autour (maquette `projet-ouvert.html`). */
export function IconeCarte({ className }: Proprietes) {
  return (
    <Icone className={className}>
      <circle cx="12" cy="12" r="3" />
      <circle cx="5" cy="6" r="2" />
      <circle cx="19" cy="6" r="2" />
      <circle cx="6" cy="19" r="2" />
      <path d="M10 10 6.5 7.3M14 10l3.5-2.7M10 14l-2.7 3.4" />
    </Icone>
  );
}

/** La Liste : trois lignes et leurs puces (maquette `projet-ouvert.html`). */
export function IconeListe({ className }: Proprietes) {
  return (
    <Icone className={className}>
      <path d="M9 6h11M9 12h11M9 18h11" />
      <path d="M4 6h1M4 12h1M4 18h1" />
    </Icone>
  );
}

/** Deux cases reliées : la Hiérarchie dans le rail, « Ce qui coince » à l'accueil. */
export function IconeCasesReliees({ className }: Proprietes) {
  return (
    <Icone className={className}>
      <path d="M5 4h6v5H5zM13 15h6v5h-6zM8 9v8h5" />
    </Icone>
  );
}

/** Un éclair : « Tu peux faire maintenant » à l'accueil. */
export function IconeEclair({ className }: Proprietes) {
  return (
    <Icone className={className}>
      <path d="M13 3 5 14h6l-1 7 8-11h-6z" />
    </Icone>
  );
}

/** Un cintre : l'« Habit » d'un projet, dans sa tête (chantier habit-du-projet, ticket 02). */
export function IconeCintre({ className }: Proprietes) {
  return (
    <Icone className={className}>
      <path d="M10 6.5a2 2 0 1 1 2 2v1.5" />
      <path d="M12 10 3.6 16.2a1 1 0 0 0 .6 1.8h15.6a1 1 0 0 0 .6-1.8z" />
    </Icone>
  );
}

/** « Partir d'un modèle » : une fiche au coin replié (maquette `projets.html`, `.actions-p`). */
export function IconeModele({ className }: Proprietes) {
  return (
    <Icone className={className}>
      <path d="M5 4h14v11l-5 5H5z" />
      <path d="M14 20v-5h5" />
    </Icone>
  );
}

/** La loupe de la recherche de la carte (chantier modes-de-la-carte, maquette `quatre-themes.html`). */
export function IconeLoupe({ className }: Proprietes) {
  return (
    <Icone className={className}>
      <circle cx="10.5" cy="10.5" r="6.5" />
      <path d="M15.5 15.5 20 20" />
    </Icone>
  );
}

/** Le combiné du téléphone : le numéro d'un contact (maquette du téléphone `app-au-telephone.html`, `ic.tel`). */
export function IconeCombine({ className }: Proprietes) {
  return (
    <Icone className={className}>
      <path d="M5 4h4l2 5-2.5 1.5a11 11 0 0 0 5 5L15 13l5 2v4a2 2 0 0 1-2 2A16 16 0 0 1 3 6a2 2 0 0 1 2-2" />
    </Icone>
  );
}

/*
 * Les signes d'un lien (chantier liens-entre-bulles, ticket 03) : sur la carte, près d'un trait
 * cliqué, et dans la fiche (ticket 05). Au trait, comme les autres pictos.
 */

/** Montrer un lien caché : l'œil ouvert. */
export function IconeOeil({ className }: Proprietes) {
  return (
    <Icone className={className}>
      <path d="M2.5 12s3.5-6.5 9.5-6.5 9.5 6.5 9.5 6.5-3.5 6.5-9.5 6.5S2.5 12 2.5 12z" />
      <circle cx="12" cy="12" r="2.8" />
    </Icone>
  );
}

/** Cacher un lien : l'œil barré. */
export function IconeOeilBarre({ className }: Proprietes) {
  return (
    <Icone className={className}>
      <path d="M2.5 12s3.5-6.5 9.5-6.5 9.5 6.5 9.5 6.5-3.5 6.5-9.5 6.5S2.5 12 2.5 12z" />
      <circle cx="12" cy="12" r="2.8" />
      <path d="M4 20 20 4" />
    </Icone>
  );
}

/** Inverser un lien : ⇄, deux flèches qui se croisent. */
export function IconeInverser({ className }: Proprietes) {
  return (
    <Icone className={className}>
      <path d="M4 8.5h15M15.5 5l3.5 3.5-3.5 3.5M20 15.5H5M8.5 12 5 15.5 8.5 19" />
    </Icone>
  );
}

/** Supprimer un lien : la croix. */
export function IconeCroix({ className }: Proprietes) {
  return (
    <Icone className={className}>
      <path d="M6 6l12 12M18 6 6 18" />
    </Icone>
  );
}

/** Lier des bulles (chantier liens-entre-bulles, ticket 04) : deux ronds reliés par un trait. */
export function IconeLier({ className }: Proprietes) {
  return (
    <Icone className={className}>
      <circle cx="6" cy="17" r="3" />
      <circle cx="18" cy="7" r="3" />
      <path d="M8.6 15.4 15.4 8.6" />
    </Icone>
  );
}

/**
 * Le cadenas du bord droit de la Carte (chantier deplacer-les-bulles, ticket 03) : fermé, l'anse
 * posée des deux côtés ; ouvert, l'anse levée et sortie à droite.
 */
export function IconeCadenas({ className, ouvert }: Proprietes & { ouvert: boolean }) {
  return (
    <Icone className={className}>
      <rect x="5" y="11" width="14" height="10" rx="2" />
      <path d={ouvert ? 'M8 11V7a4 4 0 0 1 7.6-1.7' : 'M8 11V7a4 4 0 0 1 8 0v4'} />
      <path d="M12 15v2" />
    </Icone>
  );
}
